/*
 * product-tabs.js — Product Tabs parent block driver.
 *
 * The parent `<product-tabs-component>` server-renders flat
 * sibling pairs (one button + one panel per `_product-tab-
 * content` child block via content_for 'blocks'). This script
 * reorganises that flat output at init into the conventional
 * tabs DOM:
 *
 *   <product-tabs-component>
 *     <div class="product-tabs__header">[buttons]</div>
 *     <div class="product-tabs__content">[panels]</div>
 *   </product-tabs-component>
 *
 * The first tab gets `.is-active` (or the `data-initial-tab`
 * override) on init. Click + keyboard handlers swap the active
 * button + panel; shopify:block:select activates the tab that
 * matches the editor selection.
 *
 * Same DOM-rearrange + reinit pattern as assets/testimonials.js
 * — works because Horizon's `block.blocks` is empty at render so
 * the parent can't pre-build a header row server-side.
 */

const SELECTOR = 'product-tabs-component';
const INSTANCE_KEY = '_productTabsInstance';

class ProductTabs {
  constructor(root) {
    this.root = root;
    this.abortController = new AbortController();
    this.buttons = [];
    this.panels = [];
    this.headerEl = null;
    this.contentEl = null;

    this.init();
  }

  init() {
    // Snapshot the flat children in DOM order before reparenting.
    const flatButtons = Array.from(this.root.querySelectorAll(':scope > .product-tabs__tab-btn'));
    const flatPanels = Array.from(this.root.querySelectorAll(':scope > .product-tabs__tab-panel'));
    if (flatButtons.length === 0) return;

    this.headerEl = document.createElement('div');
    this.headerEl.className = 'product-tabs__header';
    this.headerEl.setAttribute('role', 'tablist');

    this.contentEl = document.createElement('div');
    this.contentEl.className = 'product-tabs__content';

    flatButtons.forEach((btn) => {
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', 'false');
      btn.setAttribute('tabindex', '-1');
      const key = btn.getAttribute('data-tab');
      btn.id = btn.id || `${key}-btn`;
      const panel = flatPanels.find((p) => p.getAttribute('data-tab') === key);
      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', btn.id);
        panel.setAttribute('hidden', '');
        this.panels.push(panel);
      }
      this.buttons.push(btn);
      this.headerEl.appendChild(btn);
    });

    this.panels.forEach((panel) => this.contentEl.appendChild(panel));

    // Drop any stray nodes (defensive — should be empty after the
    // button/panel moves) and place header + content in order.
    this.root.replaceChildren(this.headerEl, this.contentEl);
    this.root.classList.remove('not-loaded');
    this.root.classList.add('is-initialized');

    this._bindClicks();
    this._bindKeyboard();
    this._bindCustomizerSelect();

    // Initial active tab — first by default.
    if (this.buttons.length > 0) this._activate(this.buttons[0]);
  }

  _bindClicks() {
    this.buttons.forEach((btn) => {
      btn.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this._activate(btn);
        },
        { signal: this.abortController.signal }
      );
    });
  }

  /**
   * Standard WAI-ARIA tablist keyboard pattern: Left/Right move
   * focus between tabs; Home / End jump to first / last. Tab
   * activation follows focus.
   */
  _bindKeyboard() {
    this.headerEl.addEventListener(
      'keydown',
      (event) => {
        const key = event.key;
        if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End') {
          return;
        }
        event.preventDefault();
        const activeIdx = this.buttons.findIndex((b) => b === document.activeElement);
        let nextIdx = activeIdx;
        if (key === 'ArrowRight') nextIdx = (activeIdx + 1) % this.buttons.length;
        else if (key === 'ArrowLeft')
          nextIdx = (activeIdx - 1 + this.buttons.length) % this.buttons.length;
        else if (key === 'Home') nextIdx = 0;
        else if (key === 'End') nextIdx = this.buttons.length - 1;

        const nextBtn = this.buttons[nextIdx];
        if (nextBtn) {
          this._activate(nextBtn);
          nextBtn.focus();
        }
      },
      { signal: this.abortController.signal }
    );
  }

  /**
   * When the merchant clicks a tab child block in the customizer
   * tree, jump the tabs to it so they can see what they're
   * editing. shopify_attributes lives on the button, so
   * event.target lands on the matching button.
   */
  _bindCustomizerSelect() {
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!this.root.contains(event.target)) return;
        const btn = event.target.closest('.product-tabs__tab-btn');
        if (btn && this.buttons.includes(btn)) this._activate(btn);
      },
      { signal: this.abortController.signal }
    );
  }

  _activate(btn) {
    const targetKey = btn.getAttribute('data-tab');
    this.buttons.forEach((b) => {
      const isActive = b === btn;
      b.classList.toggle('is-active', isActive);
      b.setAttribute('aria-selected', String(isActive));
      b.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    this.panels.forEach((p) => {
      const isActive = p.getAttribute('data-tab') === targetKey;
      p.classList.toggle('is-active', isActive);
      if (isActive) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
  }

  destroy() {
    this.abortController.abort();
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new ProductTabs(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
